import formulariosImg from '@/assets/help/formularios.png';
import testeAtribuirImg from '@/assets/help/teste-atribuir.png';
import testeLinkImg from '@/assets/help/teste-link.png';
import testesImg from '@/assets/help/testes.png';
import type { HelpArticle } from '../types';

export const TESTS_ARTICLES: HelpArticle[] = [
    {
        slug: 'enviar-teste-ao-paciente',
        topicId: 'tests',
        title: 'Envie um teste para o paciente responder online',
        description:
            'Atribua uma escala ao paciente, compartilhe o link seguro e deixe o resto com o LaudoPsi.',
        updatedAt: '2026-09-07',
        keywords: ['teste', 'enviar', 'atribuir', 'link', 'escala', 'responder online', 'whatsapp'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Na página **Testes** você encontra escalas conhecidas da prática clínica, como ASRS-18, PHQ-9, GAD-7, AUDIT-C, DASS-21, BIS-11 e CAT-Q. Em vez de aplicar no papel, você envia um link e o paciente responde de onde estiver.',
            },
            {
                type: 'image',
                src: testesImg,
                alt: 'Página Testes com as escalas disponíveis e os botões Atribuir e Visualizar',
            },
            { type: 'subheading', id: 'como-enviar', text: 'Como atribuir e enviar' },
            {
                type: 'steps',
                items: [
                    'Encontre o teste desejado e clique em **Atribuir**. Se quiser ver as perguntas antes, use **Visualizar**.',
                    'Selecione o paciente na lista.',
                    'Confirme a atribuição.',
                    'Copie o link gerado ou envie direto pelo botão do **WhatsApp**.',
                ],
            },
            {
                type: 'image',
                src: testeAtribuirImg,
                alt: 'Modal Selecionar paciente durante a atribuição de um teste',
            },
            {
                type: 'image',
                src: testeLinkImg,
                alt: 'Confirmação de teste atribuído com o link seguro pronto para compartilhar',
            },
            {
                type: 'callout',
                tone: 'warning',
                text: 'O link é seguro e expira em 30 dias. Se o paciente não responder a tempo, atribua novamente para gerar um novo link.',
            },
            { type: 'subheading', id: 'depois', text: 'E depois que o paciente responde?' },
            {
                type: 'paragraph',
                text: 'A resposta é corrigida automaticamente conforme a norma do instrumento e fica disponível para você revisar e aprovar dentro do laudo. O guia **Revise e aprove correções automáticas** mostra esse fluxo.',
            },
        ],
    },
    {
        slug: 'correcao-automatica',
        topicId: 'tests',
        title: 'Revise e aprove correções automáticas',
        description:
            'O que acontece depois que o paciente responde um teste: correção pela norma, texto de apoio e sua aprovação final.',
        updatedAt: '2026-07-03',
        keywords: ['correção', 'automática', 'aprovar', 'resultado', 'norma', 'revisão'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Quando o paciente conclui um teste enviado pelo LaudoPsi, o trabalho pesado já foi feito: a resposta é corrigida em segundo plano de acordo com a norma do instrumento e consolidada em uma tabela e um texto de apoio.',
            },
            { type: 'subheading', id: 'fluxo', text: 'O fluxo completo' },
            {
                type: 'steps',
                items: [
                    'Você atribui o teste e envia o link ao paciente.',
                    'O paciente responde online.',
                    'A plataforma corrige a resposta e prepara a tabela de resultados com um texto interpretativo inicial.',
                    'Dentro do laudo, na etapa **Análise**, o resultado aparece para você **revisar e aprovar** antes de entrar no documento.',
                ],
            },
            {
                type: 'callout',
                tone: 'warning',
                title: 'Nada entra no laudo sem a sua aprovação',
                text: 'O resultado corrigido só é adicionado ao documento depois que você revisa e aprova. Você mantém o controle clínico do início ao fim.',
            },
            { type: 'subheading', id: 'limites', text: 'Correções e limites do plano' },
            {
                type: 'paragraph',
                text: 'Cada plano inclui uma quantidade de correções automáticas por período. Acompanhe o seu uso na página **Assinatura**.',
            },
        ],
    },
    {
        slug: 'formularios-prontos',
        topicId: 'tests',
        title: 'Use os formulários prontos do LaudoPsi',
        description:
            'Perguntas aos pais ou cuidadores e pré-anamnese adulto: quando usar cada um e como enviá-los.',
        updatedAt: '2026-09-07',
        keywords: ['formulário', 'pré-anamnese', 'pais', 'cuidadores', 'adulto', 'pronto'],
        blocks: [
            {
                type: 'paragraph',
                text: 'A página **Formulários** traz questionários prontos para coletar informações do paciente antes ou durante a avaliação. As respostas podem apoiar a escrita do laudo com IA.',
            },
            {
                type: 'image',
                src: formulariosImg,
                alt: 'Página Formulários com os formulários prontos do LaudoPsi e o histórico de atribuições',
            },
            { type: 'subheading', id: 'quais-sao', text: 'Os formulários disponíveis' },
            {
                type: 'list',
                items: [
                    '**Perguntas aos pais ou cuidadores**: indicado para pacientes menores de idade. Quem responde é o responsável.',
                    '**Pré-anamnese adulto**: enviado diretamente para pacientes adultos.',
                ],
            },
            { type: 'subheading', id: 'como-enviar', text: 'Como enviar' },
            {
                type: 'steps',
                items: [
                    'Clique em **Atribuir** no formulário desejado. Para conferir as perguntas antes, use **Visualizar**.',
                    'Selecione o paciente.',
                    'Compartilhe o link seguro gerado. Ele expira em 30 dias.',
                ],
            },
            {
                type: 'paragraph',
                text: 'O **Histórico de Atribuições**, na mesma página, mostra o status de cada envio: pendente ou concluído.',
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'As respostas desses formulários alimentam a pré-anamnese do paciente e servem de insumo para a IA nas seções do laudo.',
            },
        ],
    },
    {
        slug: 'criar-formulario-personalizado',
        topicId: 'tests',
        title: 'Crie o seu próprio formulário',
        description:
            'Monte um questionário com as suas perguntas, no estilo formulário online, e envie como qualquer outro.',
        updatedAt: '2026-09-07',
        keywords: [
            'formulário personalizado',
            'criar formulário',
            'meus formulários',
            'perguntas',
            'builder',
        ],
        blocks: [
            {
                type: 'paragraph',
                text: 'Além dos formulários prontos, você pode criar questionários do zero, com as suas seções e os seus campos, parecido com um formulário do Google. É útil para protocolos próprios do consultório.',
            },
            { type: 'subheading', id: 'como-criar', text: 'Como criar' },
            {
                type: 'steps',
                items: [
                    'Na página **Formulários**, abra a aba **Meus formulários**.',
                    'Crie um novo formulário e dê um nome a ele.',
                    'Adicione seções e perguntas nos formatos que precisar.',
                    'Salve. O formulário fica pronto para ser atribuído a qualquer paciente.',
                ],
            },
            { type: 'subheading', id: 'envio', text: 'Envio e respostas' },
            {
                type: 'paragraph',
                text: 'O envio funciona igual ao dos formulários prontos: você atribui ao paciente, compartilha o link seguro de 30 dias e acompanha a resposta no histórico de atribuições e na página do paciente.',
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'O envio de formulários e testes conta para o limite de envios do seu plano. Consulte a página Assinatura para ver o seu saldo.',
            },
        ],
    },
    {
        slug: 'acompanhar-respostas',
        topicId: 'tests',
        title: 'Acompanhe o status de testes e formulários enviados',
        description:
            'Onde ver o que está pendente, o que já foi respondido e como abrir cada resposta.',
        updatedAt: '2026-07-03',
        keywords: ['respostas', 'status', 'pendente', 'concluído', 'histórico', 'atribuições'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Depois de enviar testes e formulários, você não precisa ficar perguntando ao paciente se ele respondeu. A plataforma mostra o status de tudo em dois lugares.',
            },
            { type: 'subheading', id: 'historico', text: 'No histórico de atribuições' },
            {
                type: 'paragraph',
                text: 'As páginas **Testes** e **Formulários** têm um **Histórico de Atribuições** com filtros por status: **Todos**, **Pendente** e **Concluído**. Cada linha mostra o paciente, o instrumento e a data de criação.',
            },
            { type: 'subheading', id: 'pagina-do-paciente', text: 'Na página do paciente' },
            {
                type: 'paragraph',
                text: 'A página de cada paciente reúne as abas **Formulários** e **Testes** com tudo o que foi enviado para aquela pessoa. É o melhor lugar para ver um caso específico. Clique em uma resposta concluída para abrir o conteúdo completo.',
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'Resposta de teste concluída aparece também dentro do laudo, na etapa Análise, aguardando a sua aprovação.',
            },
        ],
    },
];
